import { toast } from "@/hooks/use-toast";

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
}

import { reportUxError, trackErrorRetry, ERROR_TYPES, type ErrorType } from './analytics/error-telemetry';

// Enhanced toast functions with consistent styling and retry logic
export const adminToast = {
  success: (message: string, options?: ToastOptions) => {
    toast({
      title: options?.title || "Success",
      description: message,
      duration: options?.duration || 3000,
      variant: "default"
    });
  },

  error: (message: string, options?: ToastOptions & { onRetry?: () => void }) => {
    toast({
      title: options?.title || "Error",
      description: message,
      duration: options?.duration || 5000,
      variant: "destructive"
    });
  },

  warning: (message: string, options?: ToastOptions) => {
    toast({
      title: options?.title || "Warning",
      description: message,
      duration: options?.duration || 4000,
      variant: "warning", // ✅ Using warning variant (amber colors, WCAG AA compliant)
    });
  },

  info: (message: string, options?: ToastOptions) => {
    toast({
      title: options?.title || "Info",
      description: message,
      duration: options?.duration || 3000,
      variant: "default"
    });
  },

  loading: (message: string, options?: ToastOptions) => {
    return toast({
      title: options?.title || "Loading",
      description: message,
      duration: options?.duration || 0, // Persistent until dismissed
      variant: "default"
    });
  }
};

/**
 * Toast error with integrated UX analytics and Sentry tracking
 * 
 * TASK-007: Centralized error toast with telemetry
 * 
 * @example
 * ```ts
 * toastError('Failed to load wallet', {
 *   errorType: ERROR_TYPES.WALLET_BALANCES_FAILED,
 *   surface: 'wallet',
 *   critical: false,
 *   onRetry: () => refetch(),
 * });
 * ```
 */
export function toastError(
  message: string,
  options: {
    errorType: ErrorType | string;
    surface: string;
    critical?: boolean;
    onRetry?: () => void;
    title?: string;
    duration?: number;
  }
): void {
  const { errorType, surface, critical = false, onRetry, title = 'Error', duration = 5000 } = options;
  
  // Track error using centralized helper
  const normalizedErrorType = (typeof errorType === 'string' && errorType in ERROR_TYPES)
    ? ERROR_TYPES[errorType as keyof typeof ERROR_TYPES]
    : ERROR_TYPES.UNKNOWN_ERROR;
  
  reportUxError(new Error(message), {
    type: normalizedErrorType,
    surface,
    critical,
  });
  
  // Show toast with optional retry action
  toast({
    title,
    description: message,
    variant: 'destructive',
    duration,
    action: onRetry ? {
      label: 'Retry',
      onClick: () => {
        trackErrorRetry(normalizedErrorType, surface);
        onRetry();
      },
    } : undefined,
  });
}

// Retry policy for failed operations
interface RetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000
  } = options;

  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxAttempts) {
        throw lastError;
      }

      // Exponential backoff with jitter
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000,
        maxDelay
      );

      adminToast.warning(
        `Attempt ${attempt} failed, retrying in ${Math.round(delay / 1000)}s...`,
        { title: "Retry" }
      );

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

// Toast for admin operations
export const adminOperationToast = {
  dataExport: {
    start: (count: number) => adminToast.loading(
      `Exporting ${count} records...`,
      { title: "Export Started" }
    ),
    complete: (filename: string) => adminToast.success(
      `Export completed: ${filename}`,
      { title: "Export Complete" }
    ),
    error: (error: string, onRetry?: () => void) => adminToast.error(
      `Export failed: ${error}`,
      { title: "Export Failed", onRetry }
    )
  },

  dataSync: {
    start: () => adminToast.loading("Syncing data...", { title: "Sync Started" }),
    complete: (syncedCount: number) => adminToast.success(
      `Synced ${syncedCount} records successfully`,
      { title: "Sync Complete" }
    ),
    error: (error: string, onRetry?: () => void) => adminToast.error(
      `Sync failed: ${error}`,
      { title: "Sync Failed", onRetry }
    )
  },

  bulkOperation: {
    start: (operation: string, count: number) => adminToast.loading(
      `${operation} ${count} items...`,
      { title: "Bulk Operation" }
    ),
    complete: (operation: string, count: number) => adminToast.success(
      `${operation} completed for ${count} items`,
      { title: "Operation Complete" }
    ),
    error: (operation: string, error: string, onRetry?: () => void) => adminToast.error(
      `${operation} failed: ${error}`,
      { title: "Operation Failed", onRetry }
    )
  }
};